import React, { useState, useEffect } from "react";
import { Fab, Zoom } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

interface ScrollTopButtonProps {
  threshold?: number;
  bottom?: number;
  right?: number;
  zIndex?: number;
}

/**
 * Componente de botão para voltar ao topo da página
 * @param threshold - Altura em pixels a partir da qual o botão será mostrado (padrão: 20)
 * @param bottom - Posição em pixels a partir do fundo da página (padrão: 24)
 * @param right - Posição em pixels a partir da direita da página (padrão: 24)
 * @param zIndex - Índice z para o botão (padrão: 1000)
 */
const ScrollTopButton = React.memo(({
  threshold = 20,
  bottom = 24,
  right = 24,
  zIndex = 1000
}: ScrollTopButtonProps) => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const checkScrollPosition = () => {
      setShowScrollTop(window.scrollY > threshold);
    };

    // Adicionar event listener com throttle
    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkScrollPosition();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener("scroll", scrollListener);
    
    // Verificar posição inicial
    checkScrollPosition();
    
    // Limpar event listener ao desmontar
    return () => window.removeEventListener("scroll", scrollListener);
  }, [threshold]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <Zoom in={showScrollTop}>
      <Fab 
        color="primary" 
        size="medium" 
        aria-label="voltar ao topo"
        onClick={scrollToTop}
        sx={{
          position: 'fixed',
          bottom,
          right,
          zIndex,
          width: 48,
          height: 48,
          minHeight: '48px',
          minWidth: '48px',
          boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)'
        }}
      >
        <KeyboardArrowUpIcon fontSize="medium" />
      </Fab>
    </Zoom>
  );
});

export default ScrollTopButton; 